import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAccessToken } from '../lib/setupAxiosAuth';

const GOOGLE_OAUTH_POPUP_NAME = 'google-oauth-popup';
const GOOGLE_OAUTH_POPUP_WIDTH = 520;
const GOOGLE_OAUTH_POPUP_HEIGHT = 720;

function buildPopupFeatures() {
  const left = window.screenX + Math.max((window.outerWidth - GOOGLE_OAUTH_POPUP_WIDTH) / 2, 0);
  const top = window.screenY + Math.max((window.outerHeight - GOOGLE_OAUTH_POPUP_HEIGHT) / 2, 0);
  return `width=${GOOGLE_OAUTH_POPUP_WIDTH},height=${GOOGLE_OAUTH_POPUP_HEIGHT},left=${left},top=${top}`;
}

function useOAuthStatus(onConnectedEmail) {
  const [connection, setConnection] = useState({ connected: false });
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/auth/google/status');
      setConnection(response.data);
      if (response.data.connected && response.data.email) {
        onConnectedEmail?.(response.data.email);
      }
    } catch {
      setConnection({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [onConnectedEmail]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  return { connection, setConnection, loading, loadStatus };
}

function useOAuthPopupListener(loadStatus, setIsConnecting) {
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'google-oauth-success') {
        toast.success('Compte Google connecté');
        setIsConnecting(false);
        void loadStatus();
      }
      if (event.data?.type === 'google-oauth-error') {
        toast.error(event.data.message || 'Connexion Google échouée');
        setIsConnecting(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadStatus, setIsConnecting]);
}

export function useGoogleOAuthConnection(onConnectedEmail) {
  const { connection, setConnection, loading, loadStatus } = useOAuthStatus(onConnectedEmail);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useOAuthPopupListener(loadStatus, setIsConnecting);

  const connect = useCallback(() => {
    const tokenParam = getAccessToken() ? `&token=${encodeURIComponent(getAccessToken())}` : '';
    const popup = window.open(
      `/api/auth/google/login?origin=${encodeURIComponent(window.location.origin)}${tokenParam}`,
      GOOGLE_OAUTH_POPUP_NAME,
      buildPopupFeatures()
    );
    if (!popup) {
      toast.error('Popup bloquée par le navigateur');
      return;
    }
    setIsConnecting(true);
  }, []);

  const disconnect = useCallback(async () => {
    setIsDisconnecting(true);
    try {
      await axios.post('/api/auth/google/disconnect');
      setConnection({ connected: false });
      toast.success('Compte Google déconnecté');
    } catch {
      toast.error('Impossible de déconnecter le compte Google');
    } finally {
      setIsDisconnecting(false);
    }
  }, [setConnection]);

  return { connection, loading, isConnecting, isDisconnecting, connect, disconnect };
}
